<template>
  <transition name="fading">
    <Loading v-if="isLoading"/>
  </transition>

  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Workspace Registry</p>
        <h1 class="page-title">Organizations</h1>
        <p class="page-subtitle">
          Organizations are the parent workspaces. Clinics and hospitals should always sit under one.
        </p>
      </div>
      <button class="ui-button primary" @click="$router.push('/org/register')">
        <i class="fa-solid fa-plus"></i> Create organization
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Organizations</span>
        <strong class="stat-value">{{ raw_data.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Filtered</span>
        <strong class="stat-value">{{ render_data.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Setup path</span>
        <strong class="stat-value stat-text">Org → Location → Account</strong>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-search">
        <SearchBar
          searchPlaceholder="Search organization, city, state, address..."
          buttonPlaceholder="Search"
          @OutputSearch="get_search_query"
        />
      </div>
      <button class="ui-button secondary" @click="$router.push('/bulk-setup')">
        <i class="fas fa-layer-group"></i> Bulk setup
      </button>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th class="text-start">Organization</th>
            <th class="text-start">Contact</th>
            <th class="text-start">HQ Address</th>
            <th class="text-start">Fundus Camera</th>
            <th class="text-start">Subscription</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="data in paginatedData" :key="data.OrganizationUuid" @click="goto_editor(data.OrganizationUuid)" class="clickable-row">
            <td data-label="Organization">
              <span class="table-primary">{{ data.OrganizationName || 'Unnamed organization' }}</span>
              <span class="table-secondary">{{ data.OrganizationType || 'Organization type not set' }}</span>
            </td>
            <td data-label="Contact">
              <span class="table-primary">{{ data.ContactEmail || 'No email' }}</span>
              <span class="table-secondary">{{ data.ContactPhone || 'No phone' }}</span>
            </td>
            <td data-label="HQ Address">
              <span class="table-primary">{{ [data.City, data.State].filter(Boolean).join(', ') || 'No city/state' }}</span>
              <span class="table-secondary">{{ data.HQAddress || 'No HQ address yet' }}</span>
            </td>
            <td data-label="Fundus Camera">
              <span class="badge-soft" :class="data.FundusCameraName ? 'success' : 'muted'">
                {{ data.FundusCameraName || 'Not assigned' }}
              </span>
            </td>
            <td data-label="Subscription">
              <span class="badge-soft" :class="data.SubscriptionType ? 'success' : 'muted'">
                {{ data.SubscriptionType || 'Not decided' }}
              </span>
            </td>
            <td class="text-end" data-label="Actions">
              <button class="icon-button-soft" @click.stop="goto_editor(data.OrganizationUuid)" title="Edit organization">
                <i class="fas fa-pen"></i>
              </button>
            </td>
          </tr>
          <tr v-if="paginatedData.length === 0">
            <td colspan="6">
              <div class="empty-state">
                <strong>No organizations found.</strong>
                <p>Create an organization or adjust your search.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="totalPages > 1">
        <span class="table-secondary">Page {{ current_page }} of {{ totalPages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="current_page === 1" @click="changePage(current_page - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button class="icon-button-soft" :disabled="current_page === totalPages" @click="changePage(current_page + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading.vue";
import SearchBar from "@/components/SearchBar.vue";
import { getAllOrg } from "@/services/orgServices"
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

export default{
  components: {
    Loading,
    SearchBar,
    ChevronLeft,
    ChevronRight,
  },
  created(){
    this.get_data();
  },
  data() {
    return {
      raw_data: [],
      render_data: [],
      current_page: 1,
      page_limit: 10,
      isLoading: false,
    }
  },
  computed: {
    paginatedData() {
      const start = (this.current_page - 1) * this.page_limit;
      return this.render_data.slice(start, start + this.page_limit);
    },
    totalPages() {
      return Math.ceil(this.render_data.length / this.page_limit);
    },
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.current_page = page;
      }
    },
    get_search_query(search_query){
      const query = search_query ? search_query.toLowerCase() : '';
      this.current_page = 1;
      this.render_data = this.raw_data.filter((data) => {
        return [
          data.State,
          data.OrganizationName,
          data.OrganizationType,
          data.City,
          data.HQAddress,
          data.ContactEmail,
          data.FundusCameraName,
          data.SubscriptionType,
        ].some((value) => String(value || '').toLowerCase().includes(query));
      });
    },
    async get_data(){
      this.isLoading = true
      const result = await getAllOrg();

      if(result.data){
        this.raw_data = [...result.data].reverse();
        this.render_data = [...result.data].reverse();
      }
      this.isLoading = false
    },
    goto_editor(input_id){
      this.$router.push({ name: 'org-edit', params: { id: input_id} });
    },
  }
}
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}

.table-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.stat-text {
  font-size: 1rem;
}
</style>
